import {
  addCalendarYearsMonths,
  addCivilDays,
  buildHolidayMap,
  toCivilDate,
} from '../day-helpers.js';
import { buildObservedHolidays } from '../metadata-helpers.js';
import type { ResolveProceduralDateInput, ResolveProceduralDateResult } from '../types.js';

/**
 * UK E&W calendar days: every day counts (weekends, bank holidays, all). The
 * anchor itself counts as day 0 and the result is `anchor + interval` with no
 * weekend/holiday roll-over. Honours `years` / `months` / `days` on the
 * interval (with end-of-month clamping via {@link addCalendarYearsMonths}).
 */
export function resolveUkEwCalendar(
  input: ResolveProceduralDateInput,
): ResolveProceduralDateResult {
  const start = toCivilDate(input.startDate);
  const { years = 0, months = 0, days = 0 } = input.interval;

  const afterYearsMonths = addCalendarYearsMonths(start, years, months);
  const resultDate = addCivilDays(afterYearsMonths, days);

  const windowStart = resultDate.getTime() < start.getTime() ? resultDate : start;
  const windowEnd = resultDate.getTime() < start.getTime() ? start : resultDate;

  const holidayMap = buildHolidayMap(input.holidays);

  const countedDates: Date[] = [];
  if (resultDate.getTime() !== start.getTime()) {
    const forward = resultDate.getTime() > start.getTime();
    let d = addCivilDays(start, forward ? 1 : -1);
    while (forward ? d.getTime() <= resultDate.getTime() : d.getTime() >= resultDate.getTime()) {
      countedDates.push(d);
      d = addCivilDays(d, forward ? 1 : -1);
    }
  }

  return {
    resultDate,
    jurisdiction: 'uk-ew',
    dayCount: 'calendar',
    direction:
      resultDate.getTime() === start.getTime()
        ? 'same-day'
        : resultDate.getTime() > start.getTime()
          ? 'after'
          : 'before',
    startDateExcluded: false,
    endDateRolledForward: false,
    weekendsCount: true,
    publicHolidaysCount: true,
    countedDates,
    skippedDates: [],
    observedHolidays: buildObservedHolidays(holidayMap, windowStart, windowEnd),
    windowStart,
    windowEnd,
    warnings: [],
  };
}
